import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'src/redux/store';
import { UnknownAction } from 'redux';
import {
  fetchCurrentSession,
  startSession,
  endSession,
  updateSessionFromWebSocket,
} from 'src/redux/slice/cashRegister';
import { useAuthContext } from 'src/auth/hooks';
import { useLocales } from 'src/locales';
import {
  getRequiredSessionAction,
  isCashierRole,
  SessionValidationResult,
} from 'src/utils/cash-register';
import { CashRegister, CashRegisterSession } from 'src/types/cashRegister';

// ----------------------------------------------------------------------
// Module-level flag to prevent concurrent open/close API calls
// from multiple button clicks or multiple hook instances
let globalPending = false;

interface UseCashRegisterSessionReturn {
  // État
  session: CashRegisterSession | null;
  register: CashRegister | null;
  loading: boolean;
  error: string | null;
  validation: SessionValidationResult | null;
  isCashier: boolean;

  // Dialog
  dialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;

  // Actions
  refreshSession: () => Promise<void>;
  handleOpenSession: (amount: number) => Promise<void>;
  handleCloseAndReopen: (closingAmount: number, openingAmount: number) => Promise<void>;
  handleCloseSessionOnly: (closingAmount: number) => Promise<void>;

  // Vérification
  checkSession: () => boolean;
}

// ----------------------------------------------------------------------

export function useCashRegisterSession(): UseCashRegisterSessionReturn {
  const dispatch = useDispatch();
  const { t } = useLocales();
  const { user, userDataReady } = useAuthContext();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Sélecteurs Redux
  const {
    currentSession: session,
    loading: reduxLoading,
    error: reduxError,
  } = useSelector((state: any) => state.cashRegister);

  // Déterminer si l'utilisateur est caissier
  const isCashier = useMemo(() => isCashierRole(user?.role), [user?.role]);

  // Récupérer la caisse de l'utilisateur avec fallback robuste
  const userRegister = useMemo(() => {
    // Essayer plusieurs sources pour la caisse
    let register = user?.currentRegister || user?.cashRegister || null;

    // Convertir un ID en objet si nécessaire
    if (typeof register === 'string') {
      register = { id: register };
    }

    // Si register est null mais qu'on a une session, essayer de récupérer depuis la session
    if (!register && session?.cashRegister) {
      let cashRegisterId: string | undefined;

      // cashRegister peut être une chaîne (ID) ou un objet CashRegister
      if (typeof session.cashRegister === 'string') {
        cashRegisterId = session.cashRegister;
      } else if (session.cashRegister && typeof session.cashRegister === 'object' && 'id' in session.cashRegister) {
        cashRegisterId = session.cashRegister.id;
      }

      if (cashRegisterId) {
        console.log('[useCashRegisterSession] Using cashRegister from session:', cashRegisterId);
        register = { id: cashRegisterId };
      }
    }

    // Si register est toujours undefined mais qu'on a des données utilisateur, essayer de reconstruire
    if (!register && user?.id) {
      console.log('[useCashRegisterSession] No register found, user data:', {
        currentRegister: user?.currentRegister,
        cashRegister: user?.cashRegister,
        sessionCashRegister: session?.cashRegister,
        userId: user?.id
      });
    }

    return register;
  }, [user, session]);

  // Calculer la validation
  const validation = useMemo(() => {
    return getRequiredSessionAction(session, t);
  }, [session, t]);

  // Ouvrir/fermer le dialog
  const openDialog = useCallback(() => setDialogOpen(true), []);
  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setLocalError(null);
  }, []);

  // Rafraîchir la session
  const refreshSession = useCallback(async () => {
    if (!user?.id || !userDataReady) return;

    try {
      await dispatch(fetchCurrentSession({ idUser: user.id, cashRegisterId: userRegister?.id })).unwrap();
    } catch (error: any) {
      console.error('Error refreshing session:', error);
      // Ne pas propager l'erreur - c'est un cas normal si aucune session n'existe
    }
  }, [dispatch, user?.id, userDataReady, userRegister?.id]);

  // Ouvrir une nouvelle session
  const handleOpenSession = useCallback(
    async (amount: number) => {
      if (!user?.id) return;

      // Prevent concurrent API calls
      if (globalPending) {
        console.log('[handleOpenSession] Skipping: another session action in progress');
        return;
      }

      setLocalError(null);

      console.log(`handleOpenSession ${userRegister?.id} user :`, user);

      // Récupérer le cashRegisterId avec la même logique de fallback robuste
      let cashRegisterId = userRegister?.id || user?.cashRegister?.id ||
        user?.cashRegister?._id || user?.currentRegister?.id;

      if (!cashRegisterId && session?.cashRegister) {
        if (typeof session.cashRegister === 'string') {
          cashRegisterId = session.cashRegister;
        } else if (
          session.cashRegister &&
          typeof session.cashRegister === 'object' &&
          'id' in session.cashRegister
        ) {
          cashRegisterId = session.cashRegister.id;
        }
      }

      if (!cashRegisterId) {
        console.error('[handleOpenSession] No cashRegisterId available');
        setLocalError('Impossible de déterminer la caisse enregistreuse');
        return;
      }

      console.log('[handleOpenSession] Using cashRegisterId:', cashRegisterId);

      try {
        // Vérifier si une session est déjà ouverte
        await refreshSession();

        // Si une session est déjà ouverte et valide, ne pas tenter de l'ouvrir à nouveau
        if (session && session.status === 'open') {
          console.log('[handleOpenSession] Session already open, skipping open attempt');
          setDialogOpen(false);
          return;
        }

        await dispatch(
          startSession({
            idUser: user.id,
            cashRegisterId,
            openingAmount: amount,
            notes: t('cash_register.session_opening'),
          })
        ).unwrap();

        // Rafraîchir les données utilisateur après l'ouverture réussie
        console.log('[handleOpenSession] Session opened successfully, refreshing user data...');

        // Émettre un événement pour recharger les données utilisateur
        window.dispatchEvent(
          new CustomEvent('cash-register:session-opened', {
            detail: {
              userId: user.id,
              cashRegisterId,
              sessionId: session?.id,
            },
          })
        );

        // Forcer le rechargement des données utilisateur via updateCurrentUser si disponible
        if (typeof window !== 'undefined' && (window as any).updateCurrentUser) {
          try {
            await (window as any).updateCurrentUser();
            console.log('[handleOpenSession] User data refreshed via updateCurrentUser');
          } catch (refreshError) {
            console.warn(
              '[handleOpenSession] Failed to refresh user data via updateCurrentUser:',
              refreshError
            );
          }
        }

        setDialogOpen(false);
      } catch (error: any) {
        console.error('[handleOpenSession] Error:', error);

        // Si une session est déjà ouverte côté serveur, on la récupère et on continue
        const errorMessage = error.message || error;
        if (
          typeof errorMessage === 'string' &&
          (errorMessage.includes('session est déjà ouverte') ||
            errorMessage.includes('session is already open') ||
            errorMessage.includes('déjà ouverte'))
        ) {
          console.log(
            '[handleOpenSession] Session already open on server, fetching current session...'
          );
          try {
            // Récupérer la session existante du serveur
            await dispatch(fetchCurrentSession({ idUser: user.id })).unwrap();
            console.log('[handleOpenSession] Session fetched successfully, proceeding...');

            // Attendre un court délai pour que Redux se mette à jour
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Rafraîchir les données utilisateur même pour une session existante
            window.dispatchEvent(
              new CustomEvent('cash-register:session-opened', {
                detail: {
                  userId: user.id,
                  cashRegisterId: userRegister?.id,
                  sessionId: session?.id,
                },
              })
            );

            // Forcer la fermeture du dialogue après un court délai
            setTimeout(() => {
              setDialogOpen(false);
              console.log('[handleOpenSession] Dialog closed after session fetch');
            }, 200);

            return;
          } catch (fetchError: any) {
            console.error('[handleOpenSession] Failed to fetch current session:', fetchError);
            setLocalError(
              t('cash_register.errors.session_already_open_fetch_failed') ||
                'Une session est déjà ouverte. Veuillez rafraîchir la page.'
            );
            throw fetchError;
          }
        }

        setLocalError(error.message || t('cash_register.errors.session_open_error'));
        throw error;
      }
    },
    [dispatch, user?.id, userRegister?.id, t, refreshSession, session]
  );

  // Fermer et rouvrir la session
  const handleCloseAndReopen = useCallback(
    async (closingAmount: number, openingAmount: number) => {
      // MongoDB retourne _id au lieu de id
      const sessionId = session?.id || (session as any)?._id;
      console.log('[handleCloseAndReopen] Starting with:', { closingAmount, openingAmount, userId: user?.id, sessionId });

      if (!user?.id) {
        console.error('[handleCloseAndReopen] Missing user:', { userId: user?.id });
        return;
      }

      setLocalError(null);
      globalPending = true;

      try {
        // Si aucune session n'est ouverte, simplement ouvrir une nouvelle session
        if (!session || session.status !== 'open') {
          console.log('[handleCloseAndReopen] No open session, just opening new one...');
          await dispatch(
            startSession({
              idUser: user.id,
              cashRegisterId: userRegister?.id,
              openingAmount,
              notes: t('cash_register.session_opening'),
            })
          ).unwrap();
          setDialogOpen(false);

          // Check if there's a pending cash register change
          const pendingRegisterId = window.localStorage.getItem('PENDING_CASH_REGISTER_ID');
          if (pendingRegisterId) {
            console.log('[handleCloseAndReopen] Applying pending cash register change');
            window.localStorage.removeItem('PENDING_CASH_REGISTER_ID');
            // This will be handled by the auth context
          }

          return;
        }

        console.log('[handleCloseAndReopen] Step 1: Closing session...');
        // 1. Fermer la session actuelle
        const closeResult = await dispatch(
          endSession({
            idUser: user.id,
            cashRegisterId: userRegister?.id,
            closingAmount,
            notes: t('cash_register.session_closing'),
          })
        ).unwrap();
        console.log('[handleCloseAndReopen] Session closed successfully:', closeResult);

        console.log('[handleCloseAndReopen] Step 2: Opening new session...');
        // Attendre un court délai pour s'assurer que la fermeture est bien propagée
        await new Promise(resolve => setTimeout(resolve, 500));

        // 2. Ouvrir une nouvelle session
        const openResult = await dispatch(
          startSession({
            idUser: user.id,
            cashRegisterId: userRegister?.id,
            openingAmount,
            notes: t('cash_register.session_reopening'),
          })
        ).unwrap();
        console.log('[handleCloseAndReopen] Session opened successfully:', openResult);

        // Rafraîchir les données utilisateur après la réouverture réussie
        console.log('[handleCloseAndReopen] Session reopened successfully, refreshing user data...');

        // Émettre un événement pour recharger les données utilisateur
        window.dispatchEvent(new CustomEvent('cash-register:session-reopened', {
          detail: {
            userId: user.id,
            cashRegisterId: userRegister?.id,
            sessionId: session?.id
          }
        }));

        // Forcer le rechargement des données utilisateur via updateCurrentUser si disponible
        if (typeof window !== 'undefined' && (window as any).updateCurrentUser) {
          try {
            await (window as any).updateCurrentUser();
            console.log('[handleCloseAndReopen] User data refreshed via updateCurrentUser');
          } catch (refreshError) {
            console.warn('[handleCloseAndReopen] Failed to refresh user data via updateCurrentUser:', refreshError);
          }
        }

        setDialogOpen(false);

        // Check if there's a pending cash register change
        const pendingRegisterId = window.localStorage.getItem('PENDING_CASH_REGISTER_ID');
        if (pendingRegisterId) {
          console.log('[handleCloseAndReopen] Applying pending cash register change');
          window.localStorage.removeItem('PENDING_CASH_REGISTER_ID');
          // This will be handled by the auth context
        }
      } catch (error: any) {
        console.error('[handleCloseAndReopen] Error:', error);

        // Si une session est déjà ouverte côté serveur, on la récupère et on continue
        const errorMessage = error.message || error;
        if (
          typeof errorMessage === 'string' &&
          (errorMessage.includes('session est déjà ouverte') ||
            errorMessage.includes('session is already open') ||
            errorMessage.includes('déjà ouverte'))
        ) {
          console.log('[handleCloseAndReopen] Session already open on server, fetching current session...');
          try {
            // Récupérer la session existante du serveur
            await dispatch(fetchCurrentSession({ idUser: user.id })).unwrap();
            console.log('[handleCloseAndReopen] Session fetched successfully, proceeding...');

            // Rafraîchir les données utilisateur même pour une session existante
            window.dispatchEvent(new CustomEvent('cash-register:session-reopened', {
              detail: {
                userId: user.id,
                cashRegisterId: userRegister?.id,
                sessionId: session?.id
              }
            }));

            setDialogOpen(false);
            return;
          } catch (fetchError: any) {
            console.error('[handleCloseAndReopen] Failed to fetch current session:', fetchError);
            setLocalError(
              t('cash_register.errors.session_already_open_fetch_failed') ||
                'Une session est déjà ouverte. Veuillez rafraîchir la page.'
            );
            // Ne pas propager l'erreur pour éviter les UnhandledRejection
            return;
          }
        }

        setLocalError(error.message || t('cash_register.errors.session_close_reopen_error'));
        // Ne pas propager l'erreur pour éviter les UnhandledRejection
        return;
      } finally {
        globalPending = false;
      }
    },
    [dispatch, user?.id, userRegister?.id, session, t, refreshSession]
  );

  // Vérifier la session et ouvrir le dialog si nécessaire
  const checkSession = useCallback((): boolean => {
    if (validation.needsAction) {
      setDialogOpen(true);
      return false;
    }
    return true;
  }, [validation]);

  // Fermer uniquement la session (sans en rouvrir une)
  const handleCloseSessionOnly = useCallback(
    async (closingAmount: number) => {
      if (!user?.id || !session || session.status !== 'open') {
        console.log('[handleCloseSessionOnly] No open session to close');
        return;
      }

      setLocalError(null);
      globalPending = true;

      try {
        console.log('[handleCloseSessionOnly] Closing session...');
        await dispatch(
          endSession({
            idUser: user.id,
            cashRegisterId: userRegister?.id,
            closingAmount,
            notes: t('cash_register.session_closing'),
          })
        ).unwrap();
        console.log('[handleCloseSessionOnly] Session closed successfully');
        setDialogOpen(false);

        // Check if logout was triggered
        const forceCloseDialog = window.localStorage.getItem('FORCE_CLOSE_SESSION_DIALOG');
        if (forceCloseDialog === 'true') {
          window.localStorage.removeItem('FORCE_CLOSE_SESSION_DIALOG');
          console.log('[handleCloseSessionOnly] Logout triggered, proceeding with logout');
        }

        // Check if cash register change was triggered
        const pendingRegisterId = window.localStorage.getItem('PENDING_CASH_REGISTER_ID');
        if (pendingRegisterId) {
          console.log('[handleCloseSessionOnly] Applying pending cash register change');
          window.localStorage.removeItem('PENDING_CASH_REGISTER_ID');
        }
      } catch (error: any) {
        console.error('[handleCloseSessionOnly] Error:', error);
        setLocalError(error.message || t('cash_register.errors.session_close_error'));
        throw error;
      } finally {
        globalPending = false;
      }
    },
    [dispatch, user?.id, userRegister?.id, session, t]
  );

  // Pour les caissiers, vérifier automatiquement au chargement (quand userDataReady)
  useEffect(() => {
    if (isCashier && user?.id && userDataReady) {
      refreshSession().then();

      // Check if force dialog flag is set
      const forceDialog = window.localStorage.getItem('FORCE_SESSION_DIALOG');
      if (forceDialog === 'true') {
        console.log('[useCashRegisterSession] Force dialog flag detected, opening dialog');
        window.localStorage.removeItem('FORCE_SESSION_DIALOG');
        setDialogOpen(true);
      }
    }
  }, [isCashier, user?.id, userDataReady, refreshSession]);

  // Plus d'auto-ouverture : l'utilisateur doit toujours confirmer via le dialogue
  // avec saisie du montant d'ouverture. Seul refreshSession est appelé au chargement
  // pour synchroniser l'état, sans déclencher d'ouverture automatique.

  // Handle force close session dialog
  useEffect(() => {
    const forceCloseDialog = window.localStorage.getItem('FORCE_CLOSE_SESSION_DIALOG');
    if (forceCloseDialog === 'true' && session && session.status === 'open') {
      console.log('[useCashRegisterSession] Force close dialog flag detected, opening dialog in close mode');
      window.localStorage.removeItem('FORCE_CLOSE_SESSION_DIALOG');
      setDialogOpen(true);
    }
  }, [session]);

  // Fermer automatiquement le dialogue si une session valide est trouvée
  useEffect(() => {
    if (isCashier && session && session.id && !validation.needsAction && dialogOpen) {
      console.log('[useCashRegisterSession] Valid session found, closing dialog automatically');
      // Petit délai pour s'assurer que tous les états sont à jour
      const timer = setTimeout(() => {
        setDialogOpen(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isCashier, session, validation.needsAction, dialogOpen]);

  // Écouter les mises à jour de session via WebSocket
  useEffect(() => {
    const handleWsSessionUpdate = (event: CustomEvent) => {
      const sessionData = event.detail;
      console.log('[useCashRegisterSession] WebSocket session updated:', sessionData);
      if (sessionData && sessionData.id) {
        dispatch(updateSessionFromWebSocket(sessionData) as unknown as UnknownAction);
      }
    };

    window.addEventListener('ws:session-updated', handleWsSessionUpdate as EventListener);

    return () => {
      window.removeEventListener('ws:session-updated', handleWsSessionUpdate as EventListener);
    };
  }, [dispatch]);

  // Log session changes for debugging
  useEffect(() => {
    if (session) {
      console.log('[useCashRegisterSession] Current session:', {
        id: session.id,
        totalSales: session.totalSales,
        currentAmount: session.currentAmount,
        openingAmount: session.openingAmount,
        transactionCount: session.transactions?.length,
      });
    }
  }, [session]);

  return {
    // État
    session,
    register: userRegister,
    loading: reduxLoading,
    error: localError || reduxError,
    validation,
    isCashier,

    // Dialog
    dialogOpen,
    openDialog,
    closeDialog,

    // Actions
    refreshSession,
    handleOpenSession,
    handleCloseAndReopen,
    handleCloseSessionOnly,

    // Vérification
    checkSession,
  };
}
